import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { AuthService, IEntityService, tr, Key, chainAndForkJoinObservables } from '@kpd/framework-ngx';
import { Observable, forkJoin, throwError } from 'rxjs';
import { apiUrl } from 'environments/environment';
import { map, switchMap } from 'rxjs/operators';
import { Permissionset } from 'app/shared/models/permissionset';
import { IPermissionDTO } from 'app/shared/services/role.service';
import { IQueryResult, CacheService, SKIP_ERROR_HEADER_KEY } from '@kpd/framework-ngx';
import { UserManagementQueryService } from 'app/shared/services/usermanagement.query.service';
import { PermissionsetKey } from '../models/keys/permissionset-key';
import { Permission } from 'app/shared/models/permission';
import { ApplicationService } from './application.service';

export interface IPermissionSetDetailDTO {
  name: string;
  description: {
    dutch: string;
    english: string;
    french: string;
    german: string;
  };
  permissions: IPermissionDTO[];
  isKpdSet: boolean;
  type: string;
}

export interface IPermissionSetDTO {
  name: string;
  description: string;
  permissions: IPermissionDTO[];
  isKpdSet: boolean;
  type: string;
}

@Injectable()
export class PermissionsetService extends UserManagementQueryService implements IEntityService {
  constructor(
    httpClient: HttpClient,
    cache: CacheService,
    private authService: AuthService,
    private applicationService: ApplicationService,
  ) {
    super(httpClient, cache, '_permission-set');
  }

  get tenant(): Key<string> {
    return this.authService.getTenant().key;
  }

  getPermissionSetsForApplication(application: string): Observable<IQueryResult> {
    return this.httpClient
      .get<IPermissionSetDTO[]>(
        `${apiUrl()}/tenants/${this.tenant}/applications/${encodeURIComponent(application)}/permissionsets`,
      )
      .pipe(
        map((dtos: IPermissionSetDTO[]) => {
          return {
            result: dtos.map((dto) => {
              const ps = new Permissionset(dto, application);
              ps.translatedDescription = dto.description;
              return ps;
            }),
            totalCount: dtos.length,
          };
        }),
      );
  }

  get(): Observable<IQueryResult> {
    return this.applicationService.getApplicationsByTenant().pipe(
      switchMap((applications) => {
        const observers = applications.map((application) => {
          return forkJoin(
            this.httpClient
              .get<IPermissionSetDTO[]>(
                `${apiUrl()}/tenants/${this.tenant}/applications/${encodeURIComponent(
                  application.name,
                )}/permissionsets`,
              )
              .pipe(
                map((dtos) =>
                  dtos.map((dto) => {
                    return new Permissionset(dto, application.name);
                  }),
                ),
              ),
            this.httpClient
              .get<IPermissionSetDTO[]>(`${apiUrl()}/applications/${application.name}/permissionsets`)
              .pipe(
                map((dtos) =>
                  dtos
                    .map((dto) => {
                      const permission = new Permissionset(dto, application.name);
                      permission.isGlobal = true;
                      permission.setKey();
                      return permission;
                    })
                    .filter((permissionset) => permissionset.type !== 'template'),
                ),
              ),
          ).pipe(
            map((result) => {
              return ([] as Permissionset[]).concat(...result);
            }),
          );
        });
        return chainAndForkJoinObservables(observers);
      }),
      map((dtosArray: Permissionset[][]) => {
        const dtos: Permissionset[] = [].concat.apply([], dtosArray as any);
        return { result: dtos, totalCount: dtos.length };
      }),
    );
  }

  getById(id: PermissionsetKey, clearCache: boolean = false): Observable<Permissionset> {
    const permissionSetKey = id as PermissionsetKey;
    const url =
      (permissionSetKey.isGlobalPermission ? '' : `tenants/${this.tenant}/`) +
      `applications/${encodeURIComponent(permissionSetKey.applicationId)}/permissionsets/${encodeURIComponent(
        permissionSetKey.permissionSetId,
      )}`;
    return this.getDtoById(url, permissionSetKey.toString(), clearCache, (dto: IPermissionSetDetailDTO) => {
      return new Permissionset(dto, permissionSetKey.applicationId);
    });
  }

  getGlobalPermissionSets(applicationId: string): Observable<Array<Permissionset>> {
    return this.httpClient
      .get<IPermissionSetDTO[]>(`${apiUrl()}/applications/${applicationId.toString()}/permissionsets`)
      .pipe(
        map((permissionSetsDTO) =>
          permissionSetsDTO.map((permissionSetDto) => {
            const permission = new Permissionset(permissionSetDto, applicationId);
            permission.isGlobal = true;
            permission.setKey();
            return permission;
          }),
        ),
      );
  }

  save(permissionSet: Permissionset): Observable<Permissionset> {
    if (!permissionSet.isNew()) {
      this.clearCache(permissionSet.key.toString());
      // Update
      const putUrl = `${apiUrl()}/tenants/${this.tenant}/applications/${encodeURIComponent(
        permissionSet.key.applicationId,
      )}/permissionsets/${encodeURIComponent(permissionSet.key.permissionSetId)}`;

      return this.httpClient
        .put<any>(putUrl, permissionSet)
        .pipe(map((dto) => new Permissionset(dto, permissionSet.application)));
    }

    // New
    const postUrl = `${apiUrl()}/tenants/${this.tenant}/applications/${permissionSet.application}/permissionsets`;
    return this.httpClient
      .post<any>(postUrl, permissionSet, { headers: new HttpHeaders().set(SKIP_ERROR_HEADER_KEY, ['409', '400']) })
      .pipe(map((dto) => new Permissionset(dto, permissionSet.application)));
  }

  delete(id: PermissionsetKey): Observable<any> {
    const permissionSetKey = id as PermissionsetKey;
    const deleteUrl = `${apiUrl()}/tenants/${this.tenant}/applications/${encodeURIComponent(
      permissionSetKey.applicationId,
    )}/permissionsets/${encodeURIComponent(permissionSetKey.permissionSetId)}`;
    if (id.isGlobalPermission) {
      return throwError({ status: 409, error: tr('Global permissionsets can not be deleted') });
    }
    return this.httpClient.delete<any>(deleteUrl, {
      headers: new HttpHeaders().set(SKIP_ERROR_HEADER_KEY, ['409', '400']),
    });
  }

  addPermissionToPermissionSet(permissionSetKey: PermissionsetKey, permissionKey: Key<string>): Observable<any> {
    return this.httpClient.post(
      `${apiUrl()}/tenants/${this.tenant}/applications/${encodeURIComponent(
        permissionSetKey.applicationId,
      )}/permissionsets/${encodeURIComponent(permissionSetKey.permissionSetId)}/permissions`,
      Array.isArray(permissionKey) ? permissionKey.map((p) => p.toString()) : [permissionKey.id.toString()],
    );
  }

  removePermissionFromPermissionSet(
    permissionSetKey: PermissionsetKey,
    permissionKey: Key<string> | Key<string>[],
  ): Observable<any> {
    const options = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
      body: Array.isArray(permissionKey) ? permissionKey.map((p) => p.toString()) : [permissionKey.id.toString()],
    };
    return this.httpClient.delete(
      `${apiUrl()}/tenants/${this.tenant}/applications/${encodeURIComponent(
        permissionSetKey.applicationId,
      )}/permissionsets/${encodeURIComponent(permissionSetKey.permissionSetId)}/permissions`,
      options,
    );
  }

  getPermissionSetPermissions(permissionSetKey: PermissionsetKey): Observable<Permission[]> {
    return this.getById(permissionSetKey, true).pipe(map((p) => p.permissions as Permission[]));
  }

  getUserPermissionSets(application: string): Observable<Permissionset[]> {
    return this.getManageablePermissionsets(application).pipe(
      map((permissionsets) => {
        return permissionsets.map((dto) => {
          const set = new Permissionset(dto);
          set.application = application;
          set.setKey();
          return set;
        });
      }),
    );
  }

  importTemplate(
    permissionSetId: PermissionsetKey,
    types: string[],
    name: string,
    reservedNames: string[],
  ): Observable<Permissionset[]> {
    return this.getById(permissionSetId).pipe(
      switchMap((permissionSet) => {
        if (this.cache) {
          this.cache.remove(`_permission-set_${permissionSetId}`);
        }
        const ids = permissionSet.permissions ? permissionSet.permissions.map((permission) => permission.id) : [];
        const obs: Observable<Permissionset>[] = [];
        const reservedNamesCopy = [...reservedNames];
        types.forEach((type) => {
          const newPermissionSet = new Permissionset();
          newPermissionSet.type = type;
          newPermissionSet.permissionIds = ids;
          newPermissionSet.name = this.getNameForImportSet(name, reservedNamesCopy);
          newPermissionSet.description = permissionSet.description;
          newPermissionSet.application = permissionSet.application;
          reservedNamesCopy.push(newPermissionSet.name);
          obs.push(this.save(newPermissionSet));
        });
        return forkJoin(obs);
      }),
    );
  }

  getNameForImportSet(permissionSetId: string, reservedNames: string[]): string {
    let nr = 1;
    let name = permissionSetId;
    while (reservedNames.some((t) => t === name)) {
      nr++;
      name = `${permissionSetId}_${nr}`;
    }
    return name;
  }

  getManageablePermissionsets(applicationId: string): Observable<IPermissionSetDTO[]> {
    return this.httpClient.get<IPermissionSetDTO[]>(
      `${apiUrl()}/tenants/${this.tenant}/applications/${encodeURIComponent(applicationId)}/manageablepermissionsets`,
    );
  }

  getManageablePermissions(applicationId: string): Observable<Permission[]> {
    return this.httpClient.get<Permission[]>(
      `${apiUrl()}/tenants/${this.tenant}/applications/${encodeURIComponent(applicationId)}/manageablepermissions`,
    );
  }

  canManage(permissionSetKey: PermissionsetKey): Observable<boolean> {
    return this.getManageablePermissionsets(permissionSetKey.applicationId).pipe(
      map((result) => {
        return result.some(
          (permissionSet: IPermissionSetDTO) => permissionSet.name === permissionSetKey.permissionSetId,
        );
      }),
    );
  }
}
